/**
 * Applies opacity to a color.
 * @param color — string in format "#RRGGBB", "#RGB" or "rgb(r, g, b)"
 * @param opacityPercent — opacity percentage between 0 and 100
 * @returns color in "rgba(r, g, b, a)" format
 */

import type { ThemeColor } from '../../component_library/styles/theme';

export const applyOpacity = (
  color: ThemeColor,
  opacityPercent: number,
): ThemeColor => {
  // Pass through CSS keyword "inherit"
  if (color.trim().toLowerCase() === 'inherit') {
    return color;
  }

  // Clamp opacityPercent to [0, 100] and convert to [0, 1]
  const alpha = Math.max(0, Math.min(100, opacityPercent)) / 100;

  let r: number;
  let g: number;
  let b: number;

  // Handle HEX notation
  if (color.startsWith('#')) {
    let hex = color.slice(1);

    // Support short form "#RGB" by expanding each character
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }

    if (hex.length !== 6) {
      throw new Error(`Invalid hex color format: "${color}"`);
    }

    // Parse red, green, blue components
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);

    // Handle "rgb(r, g, b)" notation
  } else if (color.startsWith('rgb(')) {
    const nums = color.match(/\d+/g);
    if (!nums || nums.length < 3) {
      throw new Error(`Invalid rgb color format: "${color}"`);
    }
    [r, g, b] = nums.slice(0, 3).map((v) => parseInt(v, 10));
  } else {
    throw new Error(
      'Only formats "#RRGGBB", "#RGB" and "rgb(r, g, b)" are supported',
    );
  }

  // Return the color with the specified alpha channel
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
